import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map as rxMap, switchMap, take } from 'rxjs/operators';

import {
  UserType,
  SocialType,
  CountryType,
  MailResponseDto,
  SendValidMailGQL,
  SocialPaginatedType,
  UserEventsSourceEnum,
  FindSocialsPaginationGQL,
  UpdateCorporateUserProfileGQL,
  ProfileCompletnessProgressType,
  GetProfileCompletnessProgressGQL,
} from '@sifca-monorepo/widget-generator';
import { FetchPolicy } from '@apollo/client/core';
import { UserUpdateInput, UpdateCurrentUserGQL } from '@sifca-monorepo/widget-generator';
import {
  StateType,
  LanguageType,
  GetLanguagesCacheGQL,
  FindCountriesPaginationGQL,
  FindStatesByCountryPaginationGQL,
} from '@sifca-monorepo/widget-generator';
import { MobileAuthService } from './auth.service';
import { PointOfSaleGQL } from '@sifca-monorepo/terminal-generator';
import { PointOfSaleType, ProjectPaginateType } from '@sifca-monorepo/ecommerce-generator';

@Injectable({
  providedIn: 'root',
})
export class MobileProfileService {
  private isLast: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private isLastStates: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private isLastCountries: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private workStates: BehaviorSubject<StateType[]> = new BehaviorSubject<StateType[]>(null);
  private pos: BehaviorSubject<PointOfSaleType> = new BehaviorSubject<PointOfSaleType>(null);
  private targetFormVisible: BehaviorSubject<string> = new BehaviorSubject<string>('profile');
  private socialMedia: BehaviorSubject<SocialType[]> = new BehaviorSubject<SocialType[]>(null);
  private infiniteStates: BehaviorSubject<StateType[]> = new BehaviorSubject<StateType[]>(null);
  private languages: BehaviorSubject<LanguageType[]> = new BehaviorSubject<LanguageType[]>(null);
  private infiniteCountries: BehaviorSubject<CountryType[]> = new BehaviorSubject<CountryType[]>(null);
  private projects: BehaviorSubject<ProjectPaginateType> = new BehaviorSubject<ProjectPaginateType>(null);
  private completeProfile: BehaviorSubject<ProfileCompletnessProgressType> = new BehaviorSubject<ProfileCompletnessProgressType>(null);

  page = 0;
  limit = 50;
  searchString = '';
  notification: any;

  get pos$(): Observable<PointOfSaleType> {
    return this.pos.asObservable();
  }

  get socialMedia$(): Observable<SocialType[]> {
    return this.socialMedia.asObservable();
  }

  get targetFormVisible$(): Observable<string> {
    return this.targetFormVisible.asObservable();
  }
  set targetFormVisible$(value: any) {
    this.targetFormVisible.next(value);
  }

  get isLast$(): Observable<boolean> {
    return this.isLast.asObservable();
  }

  get isLoading$(): Observable<boolean> {
    return this.isLoading.asObservable();
  }

  get projects$(): Observable<ProjectPaginateType> {
    return this.projects.asObservable();
  }

  get infiniteStates$(): Observable<StateType[]> {
    return this.infiniteStates.asObservable();
  }

  get infiniteCountries$(): Observable<CountryType[]> {
    return this.infiniteCountries.asObservable();
  }

  get workStates$(): Observable<StateType[]> {
    return this.workStates.asObservable();
  }

  get languages$(): Observable<LanguageType[]> {
    return this.languages.asObservable();
  }

  get isLastCountries$(): Observable<boolean> {
    return this.isLastCountries.asObservable();
  }

  get isLastStates$(): Observable<boolean> {
    return this.isLastStates.asObservable();
  }

  get completeProfile$(): Observable<ProfileCompletnessProgressType> {
    return this.completeProfile.asObservable();
  }
  set completeProfile$(value: any) {
    this.completeProfile.next(value);
  }

  constructor(
    private authService: MobileAuthService,
    private pointOfSaleGQL: PointOfSaleGQL,
    private sendValidMailGQL: SendValidMailGQL,
    private getLanguagesGQL: GetLanguagesCacheGQL,
    private updateCurrentUserGQL: UpdateCurrentUserGQL,
    private findSocialsPaginationGQL: FindSocialsPaginationGQL,
    private findCountriesPaginationGQL: FindCountriesPaginationGQL,
    private updateCorporateUserProfileGQL: UpdateCorporateUserProfileGQL,
    private findStatesByCountryPaginationGQL: FindStatesByCountryPaginationGQL,
    private getProfileCompletnessProgressGQL: GetProfileCompletnessProgressGQL,
  ) {}

  extractChangedFields(input: any): string[] {
    const fields: string[] = [];

    // Basic profile fields - matching your form structure
    const basicFields = ['firstName', 'lastName', 'email', 'dateOfBirth', 'gender', 'about', 'languages', 'maritalStatus'];

    basicFields.forEach((field) => {
      if (input.hasOwnProperty(field) && input[field] !== undefined && input[field] !== null && input[field] !== '') {
        fields.push(field);
      }
    });

    // Phone field (complex object)
    if (input.phone && (input.phone.countryCode || input.phone.number)) {
      fields.push('phone');
    }

    // Complex fields with detailed tracking
    if (input.residentialAddress) {
      fields.push('residentialAddress');
      // Track specific address components based on your form structure
      const addressFields = ['address', 'postCode', 'city', 'state', 'country'];
      addressFields.forEach((subField) => {
        if (input.residentialAddress[subField]) {
          fields.push(`address_${subField}`);
        }
      });
      if (input.residentialAddress.location && input.residentialAddress.location.coordinates) {
        fields.push('address_location');
      }
    }

    if (input.socialMedia && input.socialMedia.length > 0) {
      fields.push('socialMedia');
      // Track specific social media platforms
      input.socialMedia.forEach((social) => {
        if (social.name) {
          this.socialMedia$.pipe(take(1)).subscribe((media) => {
            const platformName = this.getSocialPlatformName(social.name, media);
            if (platformName) {
              fields.push(`social_${platformName}`);
            }
          });
        }
      });
    }

    if (input.work && input.work.length > 0) {
      fields.push('work');
      // Track specific work field updates
      input.work.forEach((workItem, index) => {
        const workFields = ['company', 'position', 'from', 'to', 'current', 'description', 'city', 'country'];
        workFields.forEach((workField) => {
          if (workItem[workField] !== undefined && workItem[workField] !== null && workItem[workField] !== '') {
            fields.push(`work_${workField}`);
          }
        });
      });
    }

    if (input.education && input.education.length > 0) {
      fields.push('education');
      // Track specific education field updates
      input.education.forEach((eduItem, index) => {
        const eduFields = ['name', 'level', 'from', 'to', 'graduated', 'description', 'city', 'country'];
        eduFields.forEach((eduField) => {
          if (eduItem[eduField] !== undefined && eduItem[eduField] !== null && eduItem[eduField] !== '') {
            fields.push(`education_${eduField}`);
          }
        });
      });
    }

    if (input.interests && input.interests.length > 0) {
      fields.push('interests');
    }

    if (input.hobbies && input.hobbies.length > 0) {
      fields.push('hobbies');
    }

    // Profile picture
    if (input.avatar || input.picture) {
      fields.push('profilePicture');
    }

    return fields;
  }

  private getSocialPlatformName(socialMediaId: string, media: any[]): string {
    const platform = media?.find((m) => m.id === socialMediaId);
    return platform?.code?.toLowerCase() || 'unknown';
  }

  pointOfSale(id?: string): Observable<PointOfSaleType> {
    return this.pointOfSaleGQL.fetch({ id: id || localStorage.getItem('posId') }).pipe(
      rxMap(({ data }: any) => {
        if (data) {
          this.pos.next(data.pointOfSale);
          return data.pointOfSale;
        }
      }),
    );
  }

  getProfileCompletnessProgress(id?: string): Observable<ProfileCompletnessProgressType> {
    return this.getProfileCompletnessProgressGQL
      .fetch(
        id ? { id } : {},
        ...(localStorage.getItem('aggregatorToken')
          ? [
              {
                context: {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem('aggregatorToken')}`,
                  },
                },
              },
            ]
          : []),
      )
      .pipe(
        rxMap(({ data }: any) => {
          if (data) {
            this.completeProfile.next(data.getProfileCompletnessProgress);
            return data.getProfileCompletnessProgress;
          }
        }),
      );
  }

  findCountriesPagination(fetchPolicy: FetchPolicy = 'cache-first'): Observable<CountryType[]> {
    return this.findCountriesPaginationGQL.fetch({ pagination: { page: 0, limit: 10000 } }, { fetchPolicy }).pipe(
      rxMap(({ data }: any) => {
        this.infiniteCountries.next([...(this.infiniteCountries.value || []), ...data.findCountriesPagination?.objects]);
        this.isLastCountries.next(data.findCountriesPagination.isLast);
        return data.findCountriesPagination.objects;
      }),
    );
  }

  findStatesByCountryPagination(country: string, fetchPolicy: FetchPolicy = 'cache-first'): Observable<StateType[]> {
    return this.findStatesByCountryPaginationGQL.fetch({ country, pagination: { page: 0, limit: 10000 } }, { fetchPolicy }).pipe(
      rxMap(({ data }: any) => {
        if (data) {
          this.isLastStates.next(data.findStatesByCountryPagination?.isLast);
          this.infiniteStates.next(data.findStatesByCountryPagination?.objects);
          return data.findStatesByCountryPagination.objects;
        }
      }),
    );
  }

  findSocialsPagination(): Observable<SocialPaginatedType> {
    return this.findSocialsPaginationGQL.fetch({}).pipe(
      rxMap((response: any) => {
        this.socialMedia.next(response.data.findSocialsPagination.objects);
        return response.data.findSocialsPagination;
      }),
    );
  }

  findWorkStatesByCountryPagination(country: string): Observable<StateType[]> {
    return this.findStatesByCountryPaginationGQL.fetch({ pagination: { limit: 10000, page: 0 }, country }).pipe(
      rxMap(({ data }: any) => {
        if (data) {
          this.workStates.next(data.findStatesByCountryPagination.objects);
          return data.findStatesByCountryPagination.objects;
        }
      }),
    );
  }

  getLanguages(fetchPolicy: FetchPolicy = 'network-only'): Observable<LanguageType> {
    return this.getLanguagesGQL.fetch({}, { fetchPolicy }).pipe(
      rxMap(({ data }: any) => {
        this.languages.next(data?.getLanguagesCache);
        return data?.getLanguagesCache;
      }),
    );
  }

  updateCurrentUser(input: UserUpdateInput): Observable<UserType> {
    return this.updateCurrentUserGQL.mutate({ input, source: UserEventsSourceEnum.WIDGET_WEB }).pipe(
      switchMap(({ data }) => {
        const updatedUser = data.updateCurrentUser;
        if (localStorage.getItem('aggregatorToken')) {
          return this.updateCurrentUserGQL.mutate(
            { input, source: UserEventsSourceEnum.WIDGET_WEB },
            {
              context: {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('aggregatorToken')}`,
                },
              },
            },
          );
        }
        return of(updatedUser);
      }),
      rxMap((updatedUser: any) => {
        if (updatedUser) {
          this.authService.currentUser$ = updatedUser;
          return updatedUser;
        }
        return of(null);
      }),
    );
  }

  updateCorporateUserProfile(input: UserUpdateInput): Observable<UserType> {
    return this.authService.currentUser$.pipe(
      take(1),
      switchMap((currentUser) => {
        return this.updateCorporateUserProfileGQL.mutate({ input, id: currentUser?.id }).pipe(
          switchMap(({ data }) => {
            const updatedUser = data.updateCorporateUserProfile;
            if (localStorage.getItem('aggregatorToken')) {
              return this.updateCorporateUserProfileGQL.mutate(
                { input, id: localStorage.getItem('aggregatorUser') },
                {
                  context: {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem('aggregatorToken')}`,
                    },
                  },
                },
              );
            }
            return of(updatedUser);
          }),
          rxMap((updatedUser: any) => {
            if (updatedUser) {
              this.authService.currentUser$ = updatedUser;
              return updatedUser;
            }
            return of(null);
          }),
        );
      }),
    );
  }

  sendValidMail(): Observable<MailResponseDto> {
    return this.sendValidMailGQL.mutate({ subject: 'Please validate your email in Sifca' }).pipe(
      rxMap((response: any) => {
        return response.data.sendValidMail;
      }),
    );
  }
}
